import React, { useEffect, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import '../styles/brandbook.css';

// A single flippable page. react-pageflip requires each page to forward its ref
// to the root element so the engine can measure and animate it.
const FlipPage = React.forwardRef(({ src, label }, ref) => (
  <div className="brandbook-page" ref={ref}>
    <img src={src} alt={label} draggable={false} loading="lazy" />
  </div>
));
FlipPage.displayName = 'FlipPage';

const BrandBookFlip = ({ pages = [] }) => {
  const bookRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [current, setCurrent] = useState(0);
  // Document aspect ratio (width / height), measured from the first page so the
  // flipbook adapts to portrait booklets and landscape documents alike.
  const [aspect, setAspect] = useState(null);

  // react-snap prerenders this route; only mount the interactive flipbook on the
  // client so the prerendered HTML stays a simple static image (good for SEO and
  // avoids hydration mismatches from the engine's DOM measurements).
  useEffect(() => {
    setMounted(true);
  }, []);

  // Measure the real document proportions from the first page.
  useEffect(() => {
    if (!pages.length) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = pages[0];
  }, [pages]);

  if (!pages.length) return null;

  const total = pages.length;
  const goPrev = () => bookRef.current?.pageFlip()?.flipPrev();
  const goNext = () => bookRef.current?.pageFlip()?.flipNext();

  // Static fallback for prerender / no-JS and until the aspect ratio is known:
  // show the first page (cover). Waiting for `aspect` avoids a flash at the wrong
  // proportions before the flipbook mounts.
  if (!mounted || !aspect) {
    return (
      <div className="brandbook-wrap">
        <div className="brandbook-static">
          <img src={pages[0]} alt="Booklet cover" />
        </div>
      </div>
    );
  }

  // Landscape/square documents (e.g. 1920x1080) flip one full page at a time;
  // portrait pages read as a two-up spread like a printed booklet.
  const isLandscape = aspect >= 1;
  const baseW = isLandscape ? 900 : 460;
  const width = Math.round(baseW);
  const height = Math.round(baseW / aspect);

  // StPageFlip renders portrait (single page) when blockWidth < minWidth * 2 and
  // usePortrait is on. A huge minWidth forces single-page for landscape; a small
  // one keeps the two-up spread for portrait booklets on wide screens.
  const minWidth = isLandscape ? 2000 : 240;
  const maxWidth = isLandscape ? width : Math.min(width + 100, 560);
  const minHeight = Math.round(minWidth / aspect);
  const maxHeight = Math.round(maxWidth / aspect);

  return (
    <div className="brandbook-wrap">
      <HTMLFlipBook
        key={isLandscape ? 'landscape' : 'portrait'}
        ref={bookRef}
        width={width}
        height={height}
        size="stretch"
        minWidth={minWidth}
        maxWidth={maxWidth}
        minHeight={minHeight}
        maxHeight={maxHeight}
        drawShadow
        maxShadowOpacity={0.4}
        showCover={!isLandscape}
        mobileScrollSupport
        useMouseEvents
        showPageCorners
        flippingTime={750}
        className="brandbook-book"
        onFlip={(e) => setCurrent(e.data)}
      >
        {pages.map((src, i) => (
          <FlipPage key={i} src={src} label={`Booklet page ${i + 1}`} />
        ))}
      </HTMLFlipBook>

      <div className="brandbook-controls">
        <button
          type="button"
          onClick={goPrev}
          disabled={current <= 0}
          aria-label="Previous page"
          className="brandbook-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <span className="brandbook-counter">
          {Math.min(current + 1, total)} / {total}
          <span className="brandbook-hint">drag or click to flip</span>
        </span>

        <button
          type="button"
          onClick={goNext}
          disabled={current >= total - 1}
          aria-label="Next page"
          className="brandbook-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default BrandBookFlip;
